import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Input,
  Avatar,
  Snackbar,
  Alert,
  LinearProgress,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Helper } from "../../../../tools/Helper";
import { api_Routes } from "../../../../api_Routes";
import { useNavigate, useParams } from "react-router-dom";

const EditAd = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingAd, setIsLoadingAd] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    title: false,
    photos: false,
    price: false,
    contact: false,
    ad_sub_category_type_id: false,
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [formData, setFormData] = useState({
    description: "",
    is_ad: 1,
    price: "",
    is_show_price: 1,
    api_price: "",
    is_show_api_price: 1,
    address: "",
    video_url: "",
    site_url: "",
    phone: "",
    email: "",
    ad_sub_category_type_id: "",
  });

  // Load ad data
  useEffect(() => {
    const fetchAdData = async () => {
      setIsLoadingAd(true);
      try {
        // First fetch categories
        const categoriesResponse = await Helper.Get({
          url: api_Routes.UserAds.getCategories,
          hasToken: true,
        });

        if (categoriesResponse?.response?.data) {
          setCategories(categoriesResponse.response.data);
        }

        // Then fetch the ad data
        const adResponse = await Helper.Get({
          url: `${api_Routes.UserAds.GetOne}${id}`,
          hasToken: true,
        });
        if (adResponse?.response?.data) {
          const ad = adResponse.response.data;

          // Set basic fields
          setTitle(ad.title);
          setExistingPhotos(ad.ad_photos || []);

          // Find and set the category from the nested response
          const categoryId = ad.ad_sub_category_type?.ad_sub_category?.ad_categories?.id;
          const category = categoriesResponse?.response?.data?.find(
              cat => cat.id === categoryId
          );
          setSelectedCategory(category);

          // Find and set the subcategory from the nested response
          const subCategoryId = ad.ad_sub_category_type?.ad_sub_category?.id;
          if (category && subCategoryId) {
            const subCategory = category.subcategories.find(
                sub => sub.id === subCategoryId
            );
            setSelectedSubCategory(subCategory);
          }

          // Set form data
          setFormData({
            description: ad.description || "",
            is_ad: ad.is_ad ? 1 : 0,
            price: ad.price || "",
            is_show_price: ad.is_show_price ? 1 : 0,
            api_price: ad.api_price || "",
            is_show_api_price: ad.is_show_api_price ? 1 : 0,
            address: ad.address || "",
            video_url: ad.video_url || "",
            site_url: ad.site_url || "",
            phone: ad.phone || "",
            email: ad.email || "",
            ad_sub_category_type_id: ad.ad_sub_category_type_id || "",
          });
        }
      } catch (error) {
        console.error("Error loading ad data:", error);
      } finally {
        setIsLoadingAd(false);
      }
    };

    fetchAdData();
  }, [id]);

  const handlePhotoUpload = (e) => {
    const newFiles = Array.from(e.target.files);
    setPhotos((prevPhotos) => [...prevPhotos, ...newFiles]);
    e.target.value = "";
  };

  const removePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingPhoto = (photoId) => {
    setExistingPhotos((prev) => prev.filter((photo) => photo.id !== photoId));
    setPhotosToDelete((prev) => [...prev, photoId]);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let check = 1;
    if (type === "checkbox") {
      if (checked) check = 1;
      else check = 0;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? check : value,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    let valid = true;
    const newErrors = { ...errors };

    // Validation
    if (!title.trim()) {
      newErrors.title = true;
      valid = false;
    } else {
      newErrors.title = false;
    }

    if (photos.length === 0 && existingPhotos.length === 0) {
      newErrors.photos = true;
      valid = false;
      setOpenSnackbar(true);
    } else {
      newErrors.photos = false;
    }

    if (formData.price === "") {
      newErrors.price = true;
      valid = false;
      setOpenSnackbar(true);
    } else {
      newErrors.price = false;
    }

    if (formData.ad_sub_category_type_id === "") {
      newErrors.ad_sub_category_type_id = true;
      valid = false;
      setOpenSnackbar(true);
    } else {
      newErrors.ad_sub_category_type_id = false;
    }

    if (formData.email === "" && formData.phone === "") {
      newErrors.contact = true;
      valid = false;
      setOpenSnackbar(true);
    } else {
      newErrors.contact = false;
    }

    setErrors(newErrors);
    if (!valid) {
      setIsLoading(false);
      return;
    }

    const data = new FormData();

    // Append new photos
    photos.forEach((photo) => {
      data.append("photos[]", photo);
    });

    // Append photos to delete
    photosToDelete.forEach((photoId) => {
      data.append("photos_to_delete[]", photoId);
    });

    // Append other form data
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== "photos") {
        data.append(key, value);
      }
    });

    data.append("title", title);
    data.append("ad_category_id", selectedCategory?.id);
    data.append("ad_sub_category_id", selectedSubCategory?.id);
    data.append("_method", "PUT"); // For Laravel to handle as PUT request

    try {
      const { response, message } = await Helper.Post({
        url: `${api_Routes.UserAds.editAd}/${id}`,
        hasToken: true,
        data: data,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response) {
        navigate(`/dashboard/ads-history`); // Or wherever you want to redirect after edit
      }
    } catch (error) {
      console.error("Error updating ad:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingAd) return <LinearProgress />;

  return (
      <Box sx={{ maxWidth: 800, margin: "auto", p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Edit Your Ad
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TextField
              fullWidth
              variant="outlined"
              label="* Ad Title"
              value={title}
              error={errors.title}
              helperText={errors.title ? "Title is required" : ""}
              onChange={(e) => {
                setTitle(e.target.value);
                if (errors.title) setErrors((prev) => ({ ...prev, title: false }));
              }}
              sx={{ mb: 3 }}
          />
        </Box>

        {/* Category Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Category
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select Category</InputLabel>
            <Select
                value={selectedCategory?.id || ""}
                label="Select Category"
                onChange={(e) => {
                  const category = categories.find(cat => cat.id === e.target.value);
                  setSelectedCategory(category);
                  setSelectedSubCategory(null);
                }}
            >
              {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Subcategory Selection */}
        {selectedCategory && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Subcategory
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Select Subcategory</InputLabel>
                <Select
                    value={selectedSubCategory?.id || ""}
                    label="Select Subcategory"
                    onChange={(e) => {
                      const subCategory = selectedCategory.subcategories.find(
                          sub => sub.id === e.target.value
                      );
                      setSelectedSubCategory(subCategory);
                    }}
                >
                  {selectedCategory.subcategories.map((sub) => (
                      <MenuItem key={sub.id} value={sub.id}>
                        {sub.name}
                      </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
        )}

        {/* Subcategory Type Selection */}
        {selectedSubCategory?.subcategorytypes?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Subcategory Type
              </Typography>
              <FormControl fullWidth>
                <InputLabel>* Subcategory Type</InputLabel>
                <Select
                    value={formData.ad_sub_category_type_id}
                    label="* Subcategory Type"
                    onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          ad_sub_category_type_id: e.target.value,
                        }))
                    }
                >
                  {selectedSubCategory.subcategorytypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
        )}

        {/* Photos Section */}
        <Box sx={{ mb: 3 }}>
          {errors.photos && (
              <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 1, display: "block" }}
              >
                Please add at least one photo
              </Typography>
          )}

          <Input
              type="file"
              inputProps={{
                multiple: true,
                accept: "image/*",
              }}
              onChange={handlePhotoUpload}
              id="photo-upload"
              style={{ display: "none" }}
          />
          <label htmlFor="photo-upload">
            <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2 }}
            >
              Add More Photos
            </Button>
          </label>

          {/* Photo Previews */}
          <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                minHeight: 100,
                border: (photos.length + existingPhotos.length) ? "1px dashed #ddd" : "none",
                p: 2,
                borderRadius: 1,
              }}
          >
            {/* Existing Photos */}
            {existingPhotos.map((photo) => (
                <Box
                    key={photo.id}
                    sx={{
                      position: "relative",
                      width: 100,
                      height: 100,
                    }}
                >
                  <IconButton
                      size="small"
                      onClick={() => removeExistingPhoto(photo.id)}
                      sx={{
                        position: "absolute",
                        right: -10,
                        top: -10,
                        backgroundColor: "error.main",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "error.dark",
                        },
                        zIndex: 1,
                      }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <img
                      src={photo.url}
                      alt={`Existing ${photo.id}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                  />
                </Box>
            ))}

            {/* New Photos */}
            {photos.map((photo, index) => (
                <Box
                    key={`new-${index}`}
                    sx={{
                      position: "relative",
                      width: 100,
                      height: 100,
                    }}
                >
                  <IconButton
                      size="small"
                      onClick={() => removePhoto(index)}
                      sx={{
                        position: "absolute",
                        right: -10,
                        top: -10,
                        backgroundColor: "error.main",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "error.dark",
                        },
                        zIndex: 1,
                      }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <img
                      src={URL.createObjectURL(photo)}
                      alt={`Preview ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                  />
                </Box>
            ))}

            {(photos.length + existingPhotos.length) === 0 && (
                <Typography variant="body2" color="textSecondary">
                  No photos selected
                </Typography>
            )}
          </Box>
          <Typography variant="caption" color="textSecondary">
            {photos.length + existingPhotos.length} photo(s) selected
          </Typography>
        </Box>

        {/* Rest of the form fields */}
        <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleFormChange}
            multiline
            rows={4}
            sx={{ mb: 3 }}
        />

        <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
          <TextField
              fullWidth
              label="* Price"
              name="price"
              type="number"
              value={formData.price}
              onChange={handleFormChange}
              error={errors.price}
              helperText={errors.price ? "Price is required" : ""}
          />
          <FormControlLabel
              control={
                <Checkbox
                    name="is_show_price"
                    checked={formData.is_show_price}
                    onChange={handleFormChange}
                />
              }
              label="Show Price"
          />
        </Box>


        <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
          <TextField
              fullWidth
              label="Api Price"
              name="api_price"
              type="number"
              value={formData.api_price}
              onChange={handleFormChange}
          />
          <FormControlLabel
              control={
                <Checkbox
                    name="is_show_api_price"
                    checked={formData.is_show_api_price}
                    onChange={handleFormChange}
                />
              }
              label="Show Api Price"
          />
        </Box>

        <TextField
            fullWidth
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleFormChange}
            sx={{ mb: 3 }}
        />

        <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
          <TextField
              fullWidth
              label="Video URL"
              name="video_url"
              value={formData.video_url}
              onChange={handleFormChange}
          />
          <TextField
              fullWidth
              label="Website URL"
              name="site_url"
              value={formData.site_url}
              onChange={handleFormChange}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
          <TextField
              fullWidth
              label="* Phone"
              name="phone"
              value={formData.phone}
              onChange={handleFormChange}
              error={errors.contact && formData.email === ""}
          />
          <TextField
              fullWidth
              label="* Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleFormChange}
              error={errors.contact && formData.phone === ""}
          />
        </Box>

        <FormControlLabel
            control={
              <Checkbox
                  name="is_ad"
                  checked={formData.is_ad}
                  onChange={handleFormChange}
              />
            }
            label="This is an advertisement"
            sx={{ mb: 3 }}
        />

        <Snackbar
            open={openSnackbar}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
              onClose={handleCloseSnackbar}
              severity="error"
              sx={{ width: "100%" }}
          >
            * The Following is Required: <br />
            {errors.photos && (
                <b>
                  - At Least One Photo is Required
                  <br />
                </b>
            )}
            {errors.price && (
                <b>
                  - Price
                  <br />
                </b>
            )}
            {errors.title && (
                <b>
                  - Title
                  <br />
                </b>
            )}
            {errors.contact && (
                <b>
                  - One of the Email and Phone is required
                  <br />
                </b>
            )}
            {errors.ad_sub_category_type_id && (
                <b>
                  - Choose a sub category type please
                  <br />
                </b>
            )}
          </Alert>
        </Snackbar>

        {/* Submit Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
          <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isLoading}
              sx={{ mr: 2 }}
          >
            {isLoading ? "Updating..." : "Update Ad"}
          </Button>
          <Button
              variant="outlined"
              onClick={() => navigate(-1)} // Go back
          >
            Cancel
          </Button>
        </Box>
      </Box>
  );
};

export default EditAd;